import { isMarketingOwner } from './marketingAccess.js';

// Mays, who owns the pipeline: the one person who answers a line
// review, moves a deal into Processing, files the executed deeds. Lived
// in the Owner Dashboard's shared chunk; the deal rule set reads it
// from the server too now, where a page chunk cannot be imported.
export function isMaysUser(user) {
  return isMarketingOwner(user);
}

export function isBradUser(user) {
  const email = String(user?.email || '').trim().toLowerCase();
  const name = String(user?.name || '').trim().toLowerCase();
  const localPart = email.split('@')[0] || '';
  return /^brad(\b|[._+-])/.test(localPart) || /^brad\b/.test(name);
}

// Who may move a deal into Processing.
//
// Entering Processing is a judgement call with no requirement gates
// behind it, so it is restricted by person: Mays, and nobody else.
// Asked for in those words — "only allow contract to processing
// manually by me" — so there is no env override any more. A list that
// can be widened from a dashboard is not "only me"; it is "only me
// until somebody changes a setting", and the difference is the whole
// point of the rule.
//
// BOTH of his work identities, which the single-address version got
// wrong: signing in as mays@shadowlandresources.com locked him out of
// his own gate. That is the same failure migration 232 was written to
// fix for the Line Review, and the comment there is worth repeating —
// the narrowness only ever shut out the person it exists to let in.
//
// Matched as exact addresses rather than a local-part prefix (the way
// isBradUser does it): "mays" at some other domain is not necessarily
// the same person.
//
// This decides which buttons exist. The refusal that actually stops the
// move is the deals_processing_stage_guard trigger — see
// migration-236 — because a check in the browser is a suggestion.
export const PROCESSING_APPROVER_EMAILS = Object.freeze([
  'mays@paracleteoil.com',
  'mays@shadowlandresources.com',
]);

export function isProcessingApprover(user) {
  const email = String(user?.email || '').trim().toLowerCase();
  if (!email) return false;
  return PROCESSING_APPROVER_EMAILS.includes(email);
}

// Who may work the closing checklist.
//
// The Processing stage is where the money and the recorded documents
// are: the deed, the PSA assignment, the outsale conveyance, the
// seller's W-9 and payment details, confirmation title, the settlement
// statement. Asked for in those words — only admins click those items.
//
// Everything before Processing stays open to whoever is on the deal.
// An agent still works title, pricing and the offer; what they no
// longer do is open the tools that produce the instruments.
//
// Role, not a name list. The processing-approver gate above is "only
// me" and is spelled as addresses for that reason; this one is a job
// description, and a new admin should have it on their first day
// without a deploy.
export function isAdminUser(user) {
  return String(user?.role || '').trim().toLowerCase() === 'admin';
}

// Who may waive a closing requirement and move a deal to Closed anyway.
//
// Asked for directly: "allow Mays to move any deal to closed without
// fulfilling requirements."
//
// It was already meant to be possible — the close gate is waivable by
// an admin on purpose, and the comment above it explains why at length:
// a deed recorded at the courthouse and not yet scanned back is an
// ordinary Tuesday, and a gate nobody can pass is one somebody routes
// around by backdating the stage, which leaves a worse record than no
// gate at all.
//
// But the check was `authUser?.role === 'admin'`, read off the profile.
// Mays signing in as mays@shadowlandresources.com has NO profiles row
// at all, so his role is undefined and the waive path never appeared
// for him — the gate shut out the one person it was written to let
// through. The same failure as the Line Review in migration 232 and the
// deed drafters above, for the same reason.
//
// So: the admin role, OR Mays by address under any of his identities.
// isMarketingOwner is the address set that already lists them, used
// directly rather than re-exporting a second isMaysUser beside the one
// OwnerDashboard already defines from it.
export function canWaiveClosingRequirements(user) {
  return isAdminUser(user) || isMarketingOwner(user);
}

// Who drafts and uploads the instrument that conveys the minerals.
//
// Asked for by name — "Mays and phil are my team" — which makes this
// NARROWER than the admin gate it carves out of, not wider: Brad and
// David are admins and do not draft deeds. Worth saying plainly,
// because every other change in this area has loosened something.
//
// Addresses rather than the admin role, for the same reason
// PROCESSING_APPROVER_EMAILS is spelled as addresses. Mays works under
// more than one identity: Mays@ParacleteOil.com is an admin,
// mays@shadowlandresources.com has no profiles row at all, and
// mays.goza@gmail.com is a landman. A role check would lock him out of
// his own step from two of the three — the same failure migration 232
// was written to fix for the Line Review, and the comment there is
// worth repeating: the narrowness only ever shut out the person it
// exists to let in.
//
// His two WORK identities, matching PROCESSING_APPROVER_EMAILS exactly
// rather than inventing a third list. The gmail is deliberately not
// here: it is not a work identity, and it is a landman account.
//
// And "my team" is two named people, not a job description that grows
// the day somebody else is made an admin.
//
// Phil is listed under BOTH of his addresses while he moves from
// shadowlandresources.com to paracleteoil.com, for the same reason
// Mays is: this gate reads the SIGNED-IN email, which for both of them
// comes from Google (AuthContext takes `authUser.email` off the
// session, not off the profiles row).
//
// 'phil@paracleteoil.com' is GONE from this list, not kept as a third
// spare: it no longer exists in the app or at Google, so leaving it
// would be an address nobody holds sitting in a privilege allowlist on
// a domain the company controls.
//
// 'phil@shadowlandresources.com' stays while he still has two Google
// accounts attached to one Paraclete login.
//
// It was not a rename in the end. philip@paracleteoil.com was created
// as a SEPARATE Workspace account (hd: paracleteoil.com, name "Philip
// Swanson", Google sub 1068523…), and he signed in with it on 3 Nov,
// one minute after auth.users.email was set to match — so GoTrue
// linked it to his existing user rather than making a second one. The
// original shadowlandresources account (sub 1093427…) is still
// attached, and Google still asserts phil@shadowlandresources.com for
// it: his 15:54 sign-in rewrote that identity's claim back.
//
// Whichever of the two he signs in with, the SESSION carries
// auth.users.email — GoTrue does not sync it per identity, which that
// same 15:54 sign-in proved by leaving it alone — so the app shows
// philip@paracleteoil.com either way, and that is the address this
// gate actually sees.
//
// The old address is therefore belt-and-braces, not load-bearing.
// Drop it when the shadowlandresources identity is unlinked from his
// account, not before.
export const DEED_DRAFTER_EMAILS = Object.freeze([
  'mays@paracleteoil.com',
  'mays@shadowlandresources.com',
  'philip@paracleteoil.com',
  'phil@shadowlandresources.com',
]);

export function isDeedDrafter(user) {
  const email = String(user?.email || '').trim().toLowerCase();
  if (!email) return false;
  return DEED_DRAFTER_EMAILS.includes(email);
}

/**
 * The Processing checklist targets that are NOT admin-only, and who
 * may click them instead.
 *
 * Mays divided the closing paperwork in two: "my team to get the
 * mineral deed/assignment drafted and uploaded, and from their, the
 * agents acquisition managers are in charge of emailing/mailing the
 * W9, wiring payment info, and mineral deed to the seller", with the
 * acquisition managers set to all users.
 *
 * So the Seller W-9 chip opens for everybody. Its `send` route is the
 * e-sign chain that asks for the W-9 AND the wiring details in one
 * request, which is exactly the job described — and the routes inside
 * it are gated separately, so sending a blank form is open while
 * filing the SSN that comes back is not.
 *
 * The deed chip opens for everybody too, but only once a draft exists:
 * before that its action is the deed editor itself, which stays with
 * the drafters. See the action branch on closingDocParacleteConveyance.
 *
 * Seller Payment is deliberately absent. It is where the seller's bank
 * account is typed and read, and "email them the form" is already
 * covered by the W-9 chain above — so opening it would hand out account
 * numbers to buy a button that exists elsewhere.
 */
export const SELLER_SEND_CLOSING_TARGETS = Object.freeze([
  'closingDocSellerW9',
  'closingDocParacleteConveyance',
]);

export function isSellerSendTarget(target) {
  return SELLER_SEND_CLOSING_TARGETS.includes(String(target || ''));
}

// The stage whose checklist is admin-only. One constant, so the
// dispatcher that refuses the click and the chip that draws itself
// locked cannot disagree about which items are meant.
export const CLOSING_CHECKLIST_STAGE = 'Processing';

/**
 * The checklist keys and actions that only an admin may click.
 *
 * Read off the checklist rather than kept as a second list of action
 * names: an item added to Processing tomorrow is covered the day it is
 * added, and nobody has to remember to update a list somewhere else.
 *
 * Both keys and actions, because a chip dispatches its action string
 * while the uncheck control dispatches the item key — two shapes
 * through one dispatcher.
 *
 * An action shared with an earlier stage is dropped: 'closingDocs'
 * hangs off more than one chip, and locking it for the Processing one
 * would lock the Under Contract one with it, taking a tool away from
 * the agent whose stage still owns it.
 */
export function closingOnlyChecklistTargets(checklist = []) {
  const keys = new Set();
  const actions = new Set();
  const elsewhere = new Set();
  for (const item of Array.isArray(checklist) ? checklist : []) {
    const closing = item?.stage === CLOSING_CHECKLIST_STAGE;
    if (closing && item?.key) keys.add(item.key);
    if (typeof item?.action !== 'string') continue;
    if (closing) actions.add(item.action);
    else elsewhere.add(item.action);
  }
  for (const name of elsewhere) actions.delete(name);
  return { keys, actions };
}
